import React, { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Papa from 'papaparse';

const evaluationCriteriaOptions = ['Generation'];

const UploadComparisonCSV = ({
  handleCSVUpload,
  isSubmitting,
  modelOptions,
  defaultEmail,
  mode,
}) => {
  const [csvFile, setCsvFile] = useState(null);
  const [questionColumn, setQuestionColumn] = useState('');
  const [modelColumn, setModelColumn] = useState('');
  const [answerAColumn, setAnswerAColumn] = useState('');
  const [answerBColumn, setAnswerBColumn] = useState('');
  const [retrievedChunksColumn, setRetrievedChunksColumn] = useState('');
  const [columns, setColumns] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [userEmail, setUserEmail] = useState(defaultEmail || '');
  const [evaluationCriteria, setEvaluationCriteria] = useState([]);
  const [showHelp, setShowHelp] = useState(false);

  const expectedColumns = {
    questionColumn: ['question', 'query', 'prompt'],
    modelColumn: ['model', 'tenant_model', 'ai_model'],
    answerAColumn: ['answer', 'response'],
    answerBColumn: ['answer', 'response'],
    retrievedChunksColumn: ['chunks', 'data_chunks', 'retrieved_chunks'],
  };

  const findBestMatch = (csvCols, options) => {
    const lows = options.map((o) => o.toLowerCase());
    for (const col of csvCols) {
      const lc = col.toLowerCase();
      if (lows.includes(lc)) return col;
      const fuzzy = lows.find((o) => lc.includes(o));
      if (fuzzy) return col;
    }
    return '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setCsvFile(file);
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        setCsvData(data);
        const csvCols = Object.keys(data[0] || {});
        setColumns(csvCols);

        const usedCols = new Set();
        const mapUnique = (keyOptions) => {
          const match = findBestMatch(csvCols.filter(col => !usedCols.has(col)), keyOptions);
          if (match) usedCols.add(match);
          return match;
        };

        setQuestionColumn(mapUnique(expectedColumns.questionColumn));
        setModelColumn(mapUnique(expectedColumns.modelColumn));
        setAnswerAColumn(mapUnique(expectedColumns.answerAColumn));
        setAnswerBColumn(mapUnique(expectedColumns.answerBColumn));
        setRetrievedChunksColumn(mapUnique(expectedColumns.retrievedChunksColumn));
      },
      error: (err) => console.error('CSV parse error:', err),
    });
  };

  const handleCriteriaToggle = (e) => {
    const { name, checked } = e.target;
    setEvaluationCriteria((prev) =>
      checked ? [...prev, name] : prev.filter((c) => c !== name),
    );
  };

  const handleUpload = () => {
    if (!questionColumn || !answerAColumn || !answerBColumn || !retrievedChunksColumn) {
      alert('Please select all required columns: Question, Answer A, Answer B, and Retrieved Chunks.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      alert('Please enter a valid email address.');
      return;
    }

    const preparedData = csvData.map((row) => ({
      question: row[questionColumn],
      tenant_model: row[modelColumn],
      answer_a: row[answerAColumn],
      answer_b: row[answerBColumn],
      chunks: row[retrievedChunksColumn],
    }));

    handleCSVUpload({
      preparedData,
      userEmail,
      evaluationCriteria,
      mode,
    });
  };

  const ColumnSelect = ({ label, value, onChange, optional }) => (
    <FormControl fullWidth sx={{ mb: 3 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        {optional && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        {columns.map((col) => (
          <MenuItem key={col} value={col}>
            {col}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      {/* Top title + help toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Upload a Comparison CSV</Typography>
        <IconButton onClick={() => setShowHelp(!showHelp)}>
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Help section */}
      <Collapse in={showHelp}>
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: '1px dashed #999',
            borderRadius: 2,
            backgroundColor: '#f9f9f9',
          }}
        >
          <Typography variant="body2" gutterBottom>
            Use this tool to compare two answers side-by-side for the same question.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Required Columns:</strong>
            <ul>
              <li><strong>Question Column</strong>: The user prompt/query.</li>
              <li><strong>Answer A / B</strong>: The two model outputs you want to compare.</li>
              <li><strong>Retrieved Chunks</strong>: The retrieval context (if any) that the models used.</li>
            </ul>
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Optional:</strong> <em>Model Column</em> can be used to specify model metadata if useful.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Email:</strong> You will be notified when the comparison is complete.
          </Typography>
        </Box>
      </Collapse>

      {/* File selection */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button variant="outlined" component="label">
          Choose File
          <input type="file" accept=".csv" hidden onChange={handleFileChange} />
        </Button>
        <Typography variant="body2">
          {csvFile ? csvFile.name : 'No file selected'}
        </Typography>
      </Box>

      {/* Column selectors */}
      {columns.length > 0 && (
        <>
          <ColumnSelect
            label="Question Column"
            value={questionColumn}
            onChange={setQuestionColumn}
          />
          <ColumnSelect
            label="Model Column (optional)"
            value={modelColumn}
            onChange={setModelColumn}
            optional
          />
          <ColumnSelect
            label="Answer A Column"
            value={answerAColumn}
            onChange={setAnswerAColumn}
          />
          <ColumnSelect
            label="Answer B Column"
            value={answerBColumn}
            onChange={setAnswerBColumn}
          />
          <ColumnSelect
            label="Retrieved Chunks Column"
            value={retrievedChunksColumn}
            onChange={setRetrievedChunksColumn}
          />
        </>
      )}

      {/* Evaluation Criteria */}
      <Typography variant="subtitle1" sx={{ mt: 2 }}>
        Evaluation Criteria
      </Typography>
      <FormGroup sx={{ mb: 3 }}>
        {evaluationCriteriaOptions.map((opt) => (
          <FormControlLabel
            key={opt}
            control={
              <Checkbox
                checked={evaluationCriteria.includes(opt)}
                onChange={handleCriteriaToggle}
                name={opt}
              />
            }
            label={opt}
          />
        ))}
      </FormGroup>

      {/* Notification email */}
      <TextField
        label="Send notification to:"
        type="email"
        fullWidth
        value={userEmail}
        onChange={(e) => setUserEmail(e.target.value.trim())}
        sx={{ mb: 3 }}
      />

      {/* Submit */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleUpload}
        disabled={isSubmitting || !csvFile}
        fullWidth
      >
        Upload CSV
      </Button>
    </Box>
  );
};

export default UploadComparisonCSV;